import React, { useState, useEffect, useCallback, useMemo } from "react";
import TeacherHeader from "../common/TeacherHeader";
import Footer from "../common/Footer";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import { baseUrl } from "../../Utility/constants";
import { getSchoolId, fetchSubjectGroupMaster } from "../../Utility/commonFunctions";
import useCurrentSession from "../../hooks/useCurrentSession";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ResultSummaryReport = () => {
  const [schoolId, setSchoolId] = useState(null);
  const [standards, setStandards] = useState([]);
  const [sections, setSections] = useState([]);
  const [streams, setStreams] = useState([]);
  const [subjectGroups, setSubjectGroups] = useState([]);
  const [students, setStudents] = useState([]);

  const { currentSession } = useCurrentSession();

  // ✅ Fetch Students (All Subject Summary)
  const fetchStudents = useCallback(async () => {

    try {
      const { data } = await axios.get(
        // `${baseUrl}/api/MarksFeeding/StudentAllSubjectSummary`,
        `${baseUrl}/api/MarksFeeding/ResultSummaryReportByTeacherId`,
        {
          params: {
            teacherId: localStorage.getItem("userID")
          }
        }
      );

      setStudents(data || []);
    } catch {
      toast.error("Failed to load student data");
      setStudents([]);
    }
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // ✅ Group data student-wise
  const groupedData = useMemo(() => {
    const map = {};

    students.forEach(item => {
      if (!map[item.studentId]) {
        map[item.studentId] = {
          studentId: item.studentId,
          name: item.name,
          examSeatNo: item.examSeatNo,
          subjects: {}
        };
      }

      map[item.studentId].subjects[item.subjectName] = item.average;
    });

    return Object.values(map).sort((a, b) => {
      return (a.examSeatNo || "").localeCompare(b.examSeatNo || "", undefined, {
        numeric: true,
        sensitivity: "base"
      });
    });

  }, [students]);

  // ✅ Dynamic subject columns
  const subjectColumns = useMemo(() => {
    const set = new Set();

    students.forEach(student => {
      (student.subjects || []).forEach(sub => {
        set.add(sub.subjectName);
      });
    });

    return Array.from(set);
  }, [students]);

  // ✅ Dropdown config
  const dropdowns = useMemo(() => [
    { label: "Standard", name: "standardId", options: standards, labelKey: "standardName", valueKey: "id" },
    { label: "Section", name: "sectionId", options: sections, labelKey: "name", valueKey: "id" },
    { label: "Stream", name: "streamId", options: streams, labelKey: "name", valueKey: "id" },
    { label: "Group", name: "subjectGroupId", options: subjectGroups, labelKey: "subjectGroupName", valueKey: "subjectGroupId" }
  ], [standards, sections, streams, subjectGroups]);

 const exportToExcel = () => {
  if (!students.length) {
    toast.warning("No data to export");
    return;
  }

  const exportData = students.map((student) => {

    const row = {
      "Seat No": student.examSeatNo,
      "Student Name": student.name
    };

    // Dynamic Subject Columns
    subjectColumns.forEach(sub => {

      const subject = (student.subjects || []).find(
        s => s.subjectName === sub
      );

      if (!subject) {
        row[sub] = "-";
      }
      else if (subject.isGrade) {
        row[sub] = subject.grade;
      }
      else {
        row[sub] = subject.totalMarks;
      }

    });

    row["Total"] = student.totalWithGrace;
    row["Percentage"] = student.percentage;
    row["Remark"] = student.remark;

    return row;

  });

  const worksheet = XLSX.utils.json_to_sheet(exportData);

  // Auto width
  const columnWidths = Object.keys(exportData[0]).map(key => ({
    wch: Math.max(
      key.length,
      ...exportData.map(r => String(r[key] ?? "").length)
    ) + 2
  }));

  worksheet["!cols"] = columnWidths;

  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Result Summary"
  );

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array"
  });

  const fileData = new Blob(
    [excelBuffer],
    {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    }
  );

  saveAs(
    fileData,
    `Result_Summary_${new Date().getTime()}.xlsx`
  );
};
  return (
    <>
      <TeacherHeader />

      <div className="container mt-1 mb-5">

        <div className="d-flex justify-content-end">
          <button className="btn btn-success btn-sm" onClick={exportToExcel}>
            Export to Excel
          </button>
        </div>
        {/* 🔹 Table */}
        <div className="table-responsive">
          <table className="table table-striped table-hover mt-1">
            <thead className="table-dark">
              <tr>
                <th>Seat No</th>
                <th className="text-left">Name</th>
                {subjectColumns.map(sub => (
                  <th
                    key={sub}
                    style={{
                      minWidth: "190px",
                      maxWidth: "200px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      textAlign: "center",
                      verticalAlign: "middle"
                    }}
                  >{sub}</th>
                ))}

                {/* <th>Fail Count</th> */}
                <th>Total</th>
                <th>% Age</th>
                <th>Remark</th>
              </tr>
            </thead>

            <tbody>
              {students.length === 0 ? (
                <tr>
                  <td colSpan={3 + subjectColumns.length} className="text-center">
                    No Data Found
                  </td>
                </tr>
              ) : (
                students.map(student => {
                  return (
                    <tr key={student.studentId}>
                      <td>{student.examSeatNo}</td>
                      <td className="text-left">{student.name}</td>

                      {/* 🔥 Subject columns */}
                      {subjectColumns.map(sub => {
                        const subject = (student.subjects || []).find(s => s.subjectName === sub);

                        if (!subject) return <td key={sub}>-</td>;

                        const isGrade = subject.isGrade;
                        const hasGrace = subject.graceMarks > 0;
                        const isFail = !isGrade && subject.totalMarks < 35;

                        return (
                          <td
                            key={sub}
                            title={
                              isGrade
                                ? `Grade: ${subject.grade}`
                                : `Actual: ${subject.actualMarks}, Grace: ${subject.graceMarks}, Total: ${subject.totalMarks}`
                            }
                            className={`fw-bold ${isFail ? "text-danger" : ""} ${hasGrace ? "bg-warning bg-opacity-25" : ""}`}
                          >
                            {isGrade ? subject.grade : subject.totalMarks}
                          </td>
                        );
                      })}

                      {/* 🔥 NEW FIELDS FROM API */}
                      <td className="fw-bold">{student.totalWithGrace}</td>

                      <td className="fw-bold">
                        {student.percentage}
                      </td>

                      <td
                        className={
                          student.remark === "Fail"
                            ? "text-danger fw-bold"
                            : student.remark === "Promoted"
                              ? "text-primary fw-bold"
                              : "text-success fw-bold"
                        }
                      >
                        {student.remark}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

      </div>

      <Footer />
      <ToastContainer />
    </>
  );
};

export default ResultSummaryReport;